import React from 'react';
import type { TierDraft } from '../../services/packageTiers';

/**
 * Pricing strip of the package editor: one compact card per tier (name, monthly
 * price, Sell switch, optional Ruby Tokens). Features per tier are ticked in
 * <FeaturesByTier>.
 *
 * The top tier (Advanced) IS the package itself: its price is the package's
 * monthly price (`topPrice`) — handleSubmit saves `formData.basePrice` as the
 * top tier's price, so that card edits that field, never the draft's copy.
 */
const TAGS: Record<1 | 2 | 3, { text: string; cls: string }> = {
  1: { text: 'Entry', cls: 'bg-stone-100 text-stone-600' },
  2: { text: 'Recommended', cls: 'bg-rose-50 text-rose-800' },
  3: { text: "Today's package", cls: 'bg-[#DC143C] text-white' },
};

export const PackageTiersSection: React.FC<{
  drafts: TierDraft[];
  onChange: (next: TierDraft[]) => void;
  topPrice: string;
  onTopPrice: (v: string) => void;
  /** Feature count of the top tier (the package's own list). */
  topCount: number;
  /** Ruby AI: one extra field per tier, rendered by the caller (Plan B Task B6). */
  renderTierExtra?: (d: TierDraft, i: number) => React.ReactNode;
}> = ({ drafts, onChange, topPrice, onTopPrice, topCount, renderTierExtra }) => {
  const patch = (i: number, p: Partial<TierDraft>) => onChange(drafts.map((d, j) => (j === i ? { ...d, ...p } : d)));
  return (
    <div className="grid gap-3 md:grid-cols-3">
      {drafts.map((d, i) => {
        const isTop = d.rank === 3;
        const on = isTop || d.sell;
        const price = isTop ? topPrice : d.priceDollars;
        const count = isTop ? topCount : d.features.length;
        const tag = TAGS[d.rank];
        return (
          <div
            key={d.rank}
            className={`rounded-xl border bg-white p-3 flex flex-col gap-2.5 transition-opacity ${isTop ? 'border-[#DC143C] ring-1 ring-[#DC143C]' : on ? 'border-rose-200' : 'border-stone-200'}`}
          >
            <div className="flex items-center gap-2">
              <input
                value={d.label}
                maxLength={40}
                onChange={(e) => patch(i, { label: e.target.value })}
                aria-label={`Tier ${d.rank} name`}
                className="min-w-0 flex-1 h-9 rounded-lg border border-transparent hover:border-stone-200 focus:border-stone-400 px-2 -ml-2 text-sm font-semibold text-stone-900 bg-transparent focus:outline-none"
              />
              <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${tag.cls}`}>{tag.text}</span>
            </div>
            <div className={`flex items-baseline gap-1 ${on ? '' : 'opacity-40'}`}>
              <span className="text-sm text-stone-500">$</span>
              <input
                inputMode="decimal"
                value={price}
                disabled={!on}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9.]/g, '');
                  isTop ? onTopPrice(v) : patch(i, { priceDollars: v });
                }}
                onBlur={() => {
                  const v = (parseFloat(price || '0') || 0).toFixed(2);
                  isTop ? onTopPrice(v) : patch(i, { priceDollars: v });
                }}
                aria-label={`${d.label} monthly price`}
                className="w-28 h-10 rounded-lg border border-stone-200 focus:border-stone-500 px-2 text-2xl font-bold tabular-nums text-stone-900 focus:outline-none disabled:bg-stone-50"
              />
              <span className="text-xs text-stone-500">/month</span>
            </div>
            {renderTierExtra?.(d, i)}
            <div className="flex items-center justify-between gap-2 pt-2 border-t border-stone-100 text-xs text-stone-500">
              <span>{count} feature{count === 1 ? '' : 's'} · {isTop ? 'all current businesses' : on ? 'new sign-ups' : 'not sold'}</span>
              {!isTop && (
                <button
                  type="button"
                  role="switch"
                  aria-checked={d.sell}
                  aria-label={`Sell ${d.label}`}
                  onClick={() => patch(i, { sell: !d.sell })}
                  className="min-h-[36px] flex items-center gap-2 text-xs font-medium text-stone-700"
                >
                  Sell
                  <span className={`relative w-9 h-5 rounded-full transition-colors ${d.sell ? 'bg-[#DC143C]' : 'bg-stone-300'}`}>
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${d.sell ? 'left-[18px]' : 'left-0.5'}`} />
                  </span>
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
